import Remove from '@mui/icons-material/Remove';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import ListItem from '@mui/material/ListItem';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import type { SelectedItemRenderProps } from '@openx/components-core/lib/SelectionDrawer/types.ts';
import type { SegmentsOption } from '@openx/types/targeting/customVariables.ts';
import { CheckCircleIcon } from '@phosphor-icons/react/CheckCircle';
import { MinusCircleIcon } from '@phosphor-icons/react/MinusCircle';
import { WarningIcon } from '@phosphor-icons/react/Warning';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { formatSegmentPrice } from '../../utils/formatSegmentPrice.ts';

export type SelectedSegmentWithMode = {
  providerId: string;
  segment: SegmentsOption;
  inclusionType: 'includes' | 'excludes';
  readonly?: boolean;
  isMisconfigured?: boolean;
};

const getItemStyles = (readonly: boolean, hideRemoveUntilHover: boolean, wrapText: boolean) => ({
  padding: 1.5,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  width: '100%',
  maxWidth: '100%',
  overflow: 'hidden',
  paddingLeft: 2,
  paddingRight: wrapText ? 1.5 : 0.75,
  ...(hideRemoveUntilHover && { '& .segment-list-item .icon-button': { visibility: 'hidden' } }),
  ...(!readonly && {
    ':hover': {
      backgroundColor: 'background.boxDarker',
      ...(hideRemoveUntilHover && { '& .segment-list-item .icon-button': { visibility: 'visible' } }),
    },
  }),
});

const contentGridStyles = {
  minWidth: 0,
  overflow: 'hidden',
  flexGrow: 1,
  display: 'grid',
  gridTemplateColumns: 'auto minmax(0, 1fr)',
  columnGap: 1,
  alignItems: 'center',
};
const basicInfoBoxStyles = { gridColumn: 2, display: 'flex', flexWrap: 'wrap', gap: 1, rowGap: 0 };
const labelValuePairStyles = { display: 'flex', alignItems: 'center', mr: 0.5 };
const labelStyles = { color: 'text.secondary', mr: 0.5 };

export const SelectedContextualSegmentItem = ({
  item,
  onRemove,
  wrapText = false,
  hideRemoveUntilHover = false,
}: SelectedItemRenderProps<SelectedSegmentWithMode> & {
  wrapText?: boolean;
  hideRemoveUntilHover?: boolean;
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const priceDisplay = useMemo(
    () => formatSegmentPrice(item.segment.price_pom, item.segment.price_cpm),
    [item.segment.price_pom, item.segment.price_cpm]
  );
  const handleRemove = useCallback(() => onRemove(item), [onRemove, item]);
  const readonly = item.readonly ?? false;
  const segmentName = (
    <Typography
      variant="body1Bold"
      component="span"
      sx={{
        display: '-webkit-box',
        overflow: 'hidden',
        minWidth: 0,
        maxWidth: '100%',
        WebkitBoxOrient: 'vertical',
        WebkitLineClamp: 1,
      }}
    >
      {item.segment.name}
    </Typography>
  );

  return (
    <ListItem
      data-test={`selected-segment-${item.providerId}-${item.segment.id}`}
      sx={getItemStyles(readonly, hideRemoveUntilHover, wrapText)}
    >
      <Stack
        className="segment-list-item"
        direction="row"
        sx={{ justifyContent: 'space-between', alignContent: 'center', width: '100%', minWidth: 0 }}
      >
        <Box sx={contentGridStyles}>
          <Stack direction="row" sx={{ alignItems: 'center' }}>
            {item.inclusionType === 'includes' ? (
              <CheckCircleIcon color={theme.palette.success.main} size={20} style={{ flexShrink: 0 }} />
            ) : (
              <MinusCircleIcon color={theme.palette.error.main} size={20} style={{ flexShrink: 0 }} />
            )}
            {item.isMisconfigured ? (
              <Tooltip
                title={t(
                  'This segment comes from a contextual provider that is not connected to this account and will not deliver.'
                )}
                placement="top"
              >
                <Box
                  component="span"
                  tabIndex={0}
                  role="img"
                  aria-label={t(
                    'This segment comes from a contextual provider that is not connected to this account and will not deliver.'
                  )}
                  sx={{ display: 'inline-flex', ml: 0.5, flexShrink: 0 }}
                  data-test={`segment-misconfigured-${item.providerId}-${item.segment.id}`}
                >
                  <WarningIcon size={20} color={theme.palette.warning.main} />
                </Box>
              </Tooltip>
            ) : null}
          </Stack>
          <Box component="div" sx={{ minWidth: 0 }}>
            <Tooltip title={item.segment.name} placement="top" enterDelay={500}>
              {segmentName}
            </Tooltip>
          </Box>

          <Box sx={basicInfoBoxStyles}>
            {Boolean(priceDisplay) && (
              <Box sx={labelValuePairStyles} data-test="price-info">
                <Typography variant="body2" sx={labelStyles}>
                  {t('Price:')}
                </Typography>
                <Typography variant="body2" sx={wrapText ? { wordBreak: 'break-word' } : undefined}>
                  {priceDisplay}
                </Typography>
              </Box>
            )}
            {Boolean(item.segment.provider_name) && (
              <Box sx={labelValuePairStyles} data-test="data-partner-info">
                <Typography variant="body2" sx={labelStyles}>
                  {t('Data Partner:')}
                </Typography>
                <Typography variant="body2" sx={wrapText ? { wordBreak: 'break-word' } : undefined}>
                  {item.segment.provider_name}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        {!readonly && (
          <IconButton
            className="icon-button"
            size="small"
            onClick={handleRemove}
            aria-label={t('Remove segment')}
            data-test={`remove-segment-${item.providerId}-${item.segment.id}`}
            sx={{ alignSelf: 'center' }}
          >
            <Remove />
          </IconButton>
        )}
      </Stack>
    </ListItem>
  );
};
